import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  Mic, MicOff, Camera, StopCircle, Play, Pause, 
  Upload, FileText, ArrowLeft, Check, Loader2,
  Volume2, Trash2, RefreshCw, Download, Link2, Video, VideoOff, CheckCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import AddressAutocomplete from '../ui/AddressAutocomplete';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('rooftrack_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Retry wrapper for unreliable network (mobile in-field use)
const axiosWithRetry = async (config, maxRetries = 3) => {
  let lastError;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await axios(config);
    } catch (err) {
      lastError = err;
      const isNetworkError = !err.response && (err.message === 'Network Error' || err.code === 'ECONNABORTED');
      const isRetryable = isNetworkError || err.response?.status >= 500;
      if (!isRetryable || attempt === maxRetries) throw err;
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
      console.log(`Upload attempt ${attempt} failed (${err.message}), retrying in ${delay}ms...`);
      await new Promise(r => setTimeout(r, delay));
    }
  }
  throw lastError;
};

// Mobile-optimized FieldCam component for AI-powered damage documentation
const FieldCam = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claimId = searchParams.get('claim_id');
  const leadId = searchParams.get('lead_id');
  const leadName = searchParams.get('lead_name');
  const leadAddress = searchParams.get('lead_address');
  const { user } = useAuth();
  
  // Recording mode: 'audio' or 'video'
  const [recordingMode, setRecordingMode] = useState('audio');
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  
  // Photos state
  const [photos, setPhotos] = useState([]);
  const [photoDescriptions, setPhotoDescriptions] = useState({});
  const videoUploadRef = React.useRef(null);
  
  // Processing state
  const [isUploading, setIsUploading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [aiReport, setAiReport] = useState(null);
  const [fieldReportId, setFieldReportId] = useState(null);
  
  // UI state
  const [currentStep, setCurrentStep] = useState('record'); // record, review, report
  const [error, setError] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  
  // Test recording state
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // 'success', 'failed', null
  const [testMessage, setTestMessage] = useState('');
  const testVideoRef = useRef(null);
  const testStreamRef = useRef(null);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  
  // Load claim info if linked
  useEffect(() => {
    if (claimId) {
      loadClaimInfo();
    } else if (leadId && leadAddress) {
      // If coming from a lead, use the provided address
      setPropertyAddress(decodeURIComponent(leadAddress));
    }
  }, [claimId, leadId, leadAddress]);
  
  const loadClaimInfo = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/claims/${claimId}?user_id=${user?.id}`,
        { headers: getAuthHeaders() }
      );
      if (response.data.estimate?.address) {
        setPropertyAddress(response.data.estimate.address);
      }
    } catch (err) {
      console.error('Failed to load claim info:', err);
    }
  };
  
  // Test camera/microphone before recording
  const testRecording = async () => {
    setIsTesting(true);
    setTestResult(null);
    setTestMessage('Testing your device...');
    setError('');
    
    try {
      // Determine constraints based on current mode
      const constraints = recordingMode === 'video' 
        ? { 
            audio: { echoCancellation: true, noiseSuppression: true },
            video: { 
              facingMode: { ideal: 'environment' }, 
              width: { ideal: 640 }, 
              height: { ideal: 480 } 
            }
          }
        : { 
            audio: { echoCancellation: true, noiseSuppression: true }
          };
      
      console.log('Testing with constraints:', constraints);
      setTestMessage(recordingMode === 'video' ? 'Accessing camera and microphone...' : 'Accessing microphone...');
      
      // Request media access
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      testStreamRef.current = stream;
      
      // Show video preview if in video mode
      if (recordingMode === 'video' && testVideoRef.current) {
        testVideoRef.current.srcObject = stream;
        await testVideoRef.current.play();
      }
      
      setTestMessage('Testing recording capability...');
      
      // Test MediaRecorder
      let mimeType = '';
      if (recordingMode === 'video') {
        if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
          mimeType = 'video/webm;codecs=vp9,opus';
        } else if (MediaRecorder.isTypeSupported('video/webm')) {
          mimeType = 'video/webm';
        } else if (MediaRecorder.isTypeSupported('video/mp4')) {
          mimeType = 'video/mp4';
        }
      } else {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      const testRecorder = new MediaRecorder(stream, options);
      
      // Test that we can start and get data
      let gotData = false;
      testRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          gotData = true;
        }
      };
      
      testRecorder.start(100);
      
      // Record for 1 second then check
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      testRecorder.stop();
      
      // Wait a bit for data to be processed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Stop the stream
      stream.getTracks().forEach(track => track.stop());
      if (testVideoRef.current) {
        testVideoRef.current.srcObject = null;
      }
      testStreamRef.current = null;
      
      if (gotData) {
        setTestResult('success');
        setTestMessage(recordingMode === 'video' 
          ? '✓ Camera and microphone working! Ready to record.' 
          : '✓ Microphone working! Ready to record.');
      } else {
        setTestResult('warning');
        setTestMessage('Device access granted but no data captured. Try recording anyway.');
      }
      
    } catch (err) {
      console.error('Test recording error:', err);
      
      // Clean up any stream
      if (testStreamRef.current) {
        testStreamRef.current.getTracks().forEach(track => track.stop());
        testStreamRef.current = null;
      }
      if (testVideoRef.current) {
        testVideoRef.current.srcObject = null;
      }
      
      setTestResult('failed');
      
      // Provide specific error messages
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setTestMessage(`✗ Permission denied. Please allow ${recordingMode === 'video' ? 'camera and microphone' : 'microphone'} access in your browser settings.`);
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setTestMessage(`✗ No ${recordingMode === 'video' ? 'camera' : 'microphone'} found on this device.`);
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setTestMessage(`✗ Could not access ${recordingMode === 'video' ? 'camera/microphone' : 'microphone'}. It may be in use by another app.`);
      } else if (err.name === 'OverconstrainedError') {
        setTestMessage('✗ Camera settings not supported. Try switching to Audio Only mode.');
      } else {
        setTestMessage(`✗ Test failed: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setIsTesting(false);
    }
  };
  
  // Clear test result when mode changes
  useEffect(() => {
    setTestResult(null);
    setTestMessage('');
  }, [recordingMode]);
  
  // Start voice recording
  const startRecording = async () => {
    try {
      setError('');
      
      // Determine the best constraints based on device capabilities
      const constraints = recordingMode === 'video' 
        ? { 
            audio: { echoCancellation: true, noiseSuppression: true },
            video: { 
              facingMode: { ideal: 'environment' }, 
              width: { ideal: 1280, max: 1920 }, 
              height: { ideal: 720, max: 1080 } 
            }
          }
        : { 
            audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 }
          };
      
      console.log('Requesting media with constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      // Show video preview if recording video
      if (recordingMode === 'video' && videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', '');
        videoRef.current.setAttribute('webkit-playsinline', '');
        videoRef.current.muted = true;
        // Small delay to let the DOM update, then play
        await new Promise(r => setTimeout(r, 200));
        try {
          await videoRef.current.play();
          console.log('Video preview started successfully');
        } catch (playError) {
          console.warn('Video autoplay failed, retrying:', playError);
          // Retry after a brief pause
          await new Promise(r => setTimeout(r, 500));
          try { await videoRef.current.play(); } catch (e) { console.warn('Video play retry failed:', e); }
        }
      }
      
      // Determine supported MIME type with fallbacks
      let mimeType;
      if (recordingMode === 'video') {
        if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
          mimeType = 'video/webm;codecs=vp9,opus';
        } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
          mimeType = 'video/webm;codecs=vp8,opus';
        } else if (MediaRecorder.isTypeSupported('video/webm')) {
          mimeType = 'video/webm';
        } else if (MediaRecorder.isTypeSupported('video/mp4')) {
          mimeType = 'video/mp4';
        } else {
          mimeType = ''; // Let browser choose
        }
      } else {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else {
          mimeType = ''; // Let browser choose
        }
      }
      
      console.log('Using MIME type:', mimeType || 'browser default');
      
      const mediaRecorderOptions = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        console.log('Data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setError(`Recording error: ${event.error?.message || 'Unknown error'}`);
        stopRecording();
      };
      
      mediaRecorder.onstop = () => {
        console.log('MediaRecorder stopped, chunks:', audioChunksRef.current.length);
        
        if (audioChunksRef.current.length === 0) {
          setError('No data recorded. Please try again.');
          return;
        }
        
        const finalMimeType = mediaRecorder.mimeType || (recordingMode === 'video' ? 'video/webm' : 'audio/webm');
        const blob = new Blob(audioChunksRef.current, { type: finalMimeType });
        console.log('Created blob:', blob.size, 'bytes, type:', blob.type);
        
        if (blob.size === 0) {
          setError('Recording failed - no data captured. Please try again.');
          return;
        }
        
        const blobUrl = URL.createObjectURL(blob);
        
        if (recordingMode === 'video') {
          setVideoBlob(blob);
          setVideoUrl(blobUrl);
          setAudioBlob(blob); // Also set audioBlob for unified processing
          setAudioUrl(blobUrl);
        } else {
          setAudioBlob(blob);
          setAudioUrl(blobUrl);
        }
        
        // Stop all tracks
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('Stopped track:', track.kind);
        });
        
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      };
      
      // Start recording with shorter timeslice for more frequent data collection
      mediaRecorder.start(500); // Collect data every 500ms for reliability
      setIsRecording(true);
      setIsPaused(false);
      console.log('Recording started');
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const next = prev + 1;
          // Auto-stop at 5 minutes (300 seconds)
          if (next >= 300) {
            stopRecording();
          }
          return next;
        });
      }, 1000);
      
    } catch (err) {
      console.error('Recording error:', err);
      const mediaType = recordingMode === 'video' ? 'camera and microphone' : 'microphone';
      
      // Provide more specific error messages
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError(`${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} access denied. Please allow ${mediaType} access in your browser settings.`);
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError(`No ${mediaType} found. Please ensure your device has a ${recordingMode === 'video' ? 'camera' : 'microphone'}.`);
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError(`Could not access ${mediaType}. It may be in use by another application.`);
      } else if (err.name === 'OverconstrainedError') {
        setError(`Camera constraints not supported. Trying with default settings...`);
        // Try again with simpler constraints
        if (recordingMode === 'video') {
          try {
            const simpleStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
            streamRef.current = simpleStream;
            if (videoRef.current) {
              videoRef.current.srcObject = simpleStream;
              await videoRef.current.play();
            }
            // Continue with simplified setup...
            const simpleRecorder = new MediaRecorder(simpleStream);
            mediaRecorderRef.current = simpleRecorder;
            audioChunksRef.current = [];
            simpleRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
            simpleRecorder.start(500);
            setIsRecording(true);
            timerRef.current = setInterval(() => setRecordingTime(prev => {
              const next = prev + 1;
              if (next >= 300) stopRecording();
              return next;
            }), 1000);
            setError('');
          } catch (retryErr) {
            setError(`Could not start recording: ${retryErr.message}`);
          }
        }
      } else {
        setError(`Could not start recording: ${err.message || 'Unknown error'}`);
      }
    }
  };
  
  // Stop recording
  const stopRecording = () => {
    console.log('Stopping recording...');
    
    if (mediaRecorderRef.current && isRecording) {
      try {
        // Request final data before stopping
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.requestData();
        }
        mediaRecorderRef.current.stop();
        console.log('MediaRecorder stop called');
      } catch (err) {
        console.error('Error stopping MediaRecorder:', err);
      }
      
      setIsRecording(false);
      setIsPaused(false);
      clearInterval(timerRef.current);
      
      // Stop video preview
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log('Track stopped:', track.kind);
        });
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };
  
  // Pause/Resume recording
  const togglePause = () => {
    if (!mediaRecorderRef.current) return;
    
    if (isPaused) {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
    }
    setIsPaused(!isPaused);
  };
  
  // Clear recording
  const clearRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setVideoBlob(null);
    setVideoUrl(null);
    setRecordingTime(0);
    setTranscription('');
    audioChunksRef.current = [];
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };
  
  // Handle photo upload
  const handlePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    const newPhotos = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file),
      description: ''
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };
  
  // Remove photo
  const removePhoto = (photoId) => {
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };
  
  // Update photo description
  const updatePhotoDescription = (photoId, description) => {
    setPhotos(prev => prev.map(p => 
      p.id === photoId ? { ...p, description } : p
    ));
  };
  
  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle video upload from camera roll / file picker
  const handleVideoFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Accept video and audio files
    if (!file.type.startsWith('video/') && !file.type.startsWith('audio/')) {
      setError('Please select a video or audio file');
      return;
    }
    
    // Create a blob and set it as the recording
    const blob = new Blob([file], { type: file.type });
    if (file.type.startsWith('video/')) {
      setVideoBlob(blob);
      setAudioBlob(null);
      setRecordingMode('video');
      // Create object URL for preview
      if (videoRef.current) {
        videoRef.current.src = URL.createObjectURL(blob);
      }
    } else {
      setAudioBlob(blob);
      setVideoBlob(null);
      setRecordingMode('audio');
    }
    setCurrentStep('review');
    setError('');
    toast.success(`${file.name} loaded — ready to process`);
    e.target.value = ''; // Reset input
  };

  // Download/save the AI report as a text file
  const downloadReport = () => {
    if (!aiReport) return;
    
    let content = `FIELDCAM AI INSPECTION REPORT\n`;
    content += `${'='.repeat(50)}\n`;
    content += `Property: ${propertyAddress || 'N/A'}\n`;
    content += `Date: ${new Date().toLocaleDateString()}\n`;
    content += `Report ID: ${fieldReportId || 'N/A'}\n\n`;
    
    content += `SUMMARY\n${'-'.repeat(30)}\n${aiReport.summary || 'N/A'}\n`;
    content += `Urgency: ${aiReport.urgency?.toUpperCase() || 'N/A'}\n\n`;
    
    if (aiReport.damage_areas?.length > 0) {
      content += `DAMAGE AREAS\n${'-'.repeat(30)}\n`;
      aiReport.damage_areas.forEach((area, i) => {
        content += `${i + 1}. ${area.location} — ${area.severity}\n`;
        content += `   ${area.description}\n`;
        content += `   Type: ${area.damage_type} | Action: ${area.recommended_action}\n\n`;
      });
    }
    
    if (aiReport.materials_affected?.length > 0) {
      content += `MATERIALS AFFECTED\n${'-'.repeat(30)}\n`;
      aiReport.materials_affected.forEach(m => {
        content += `- ${m.material}: ${m.condition}${m.replacement_needed ? ' (REPLACE)' : ''}\n`;
      });
      content += '\n';
    }
    
    if (aiReport.xactimate_codes?.length > 0) {
      content += `XACTIMATE CODES\n${'-'.repeat(30)}\n`;
      aiReport.xactimate_codes.forEach(c => {
        content += `${c.code}  ${c.description}  Qty: ${c.estimated_quantity}\n`;
      });
      content += '\n';
    }
    
    if (aiReport.recommendations?.length > 0) {
      content += `RECOMMENDATIONS\n${'-'.repeat(30)}\n`;
      aiReport.recommendations.forEach(r => content += `- ${r}\n`);
      content += '\n';
    }
    
    if (aiReport.insurance_notes) {
      content += `INSURANCE NOTES\n${'-'.repeat(30)}\n${aiReport.insurance_notes}\n`;
    }
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `FieldCam_Report_${fieldReportId || 'draft'}_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  // Process recording - upload, transcribe, generate report
  const processRecording = async () => {
    const mediaBlob = videoBlob || audioBlob;
    if (!mediaBlob) {
      setError('No recording to process');
      return;
    }
    
    console.log('Processing recording, size:', mediaBlob.size, 'type:', mediaBlob.type);
    
    // Check if blob has data
    if (mediaBlob.size === 0) {
      setError('Recording is empty. Please record again.');
      return;
    }
    
    // Log file size for debugging
    const sizeMB = mediaBlob.size / (1024 * 1024);
    console.log(`Recording size: ${sizeMB.toFixed(1)}MB`);
    
    setIsUploading(true);
    setError('');
    
    try {
      // Create field report first
      console.log('Creating field report...');
      const createResponse = await axiosWithRetry({
        method: 'post',
        url: `${BACKEND_URL}/api/field-reports?user_id=${user?.id}`,
        data: {
          claim_id: claimId || null,
          property_address: propertyAddress,
          notes: ''
        },
        headers: getAuthHeaders(),
        timeout: 30000
      });
      
      const reportId = createResponse.data.id;
      setFieldReportId(reportId);
      console.log('Field report created:', reportId);
      
      // Upload audio/video - determine correct extension from blob type
      const audioFormData = new FormData();
      let fileExtension = 'webm';
      if (mediaBlob.type.includes('mp4')) {
        fileExtension = 'mp4';
      } else if (mediaBlob.type.includes('ogg')) {
        fileExtension = 'ogg';
      }
      const fileName = recordingMode === 'video' ? `recording.${fileExtension}` : `recording.${fileExtension}`;
      
      console.log('Uploading media file:', fileName, 'size:', mediaBlob.size);
      
      const CHUNK_THRESHOLD = 10 * 1024 * 1024; // 10MB
      
      if (mediaBlob.size > CHUNK_THRESHOLD) {
        // Use chunked multipart upload for large files
        console.log('Using chunked upload for large file...');
        const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
        
        // Initiate multipart
        const initForm = new FormData();
        initForm.append('file_name', fileName);
        initForm.append('folder', `field-reports/${reportId}`);
        const initRes = await axiosWithRetry({
          method: 'post',
          url: `${BACKEND_URL}/api/upload/initiate-multipart`,
          data: initForm,
          headers: getAuthHeaders(),
          timeout: 30000
        });
        const { upload_id, s3_key } = initRes.data;
        
        // Upload chunks
        const totalChunks = Math.ceil(mediaBlob.size / CHUNK_SIZE);
        const parts = [];
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, mediaBlob.size);
          const chunk = mediaBlob.slice(start, end);
          const chunkForm = new FormData();
          chunkForm.append('file', chunk, `chunk_${i + 1}`);
          chunkForm.append('upload_id', upload_id);
          chunkForm.append('s3_key', s3_key);
          chunkForm.append('part_number', String(i + 1));
          console.log(`Uploading chunk ${i + 1}/${totalChunks}...`);
          const partRes = await axiosWithRetry({
            method: 'post',
            url: `${BACKEND_URL}/api/upload/upload-chunk`,
            data: chunkForm,
            headers: getAuthHeaders(),
            timeout: 120000
          });
          parts.push(partRes.data);
        }
        
        // Complete multipart
        const completeForm = new FormData();
        completeForm.append('upload_id', upload_id);
        completeForm.append('s3_key', s3_key);
        completeForm.append('parts_json', JSON.stringify(parts));
        completeForm.append('original_filename', fileName);
        const completeRes = await axiosWithRetry({
          method: 'post',
          url: `${BACKEND_URL}/api/upload/complete-multipart`,
          data: completeForm,
          headers: getAuthHeaders(),
          timeout: 30000
        });
        
        // Update the field report with the S3 URL
        await axios.put(
          `${BACKEND_URL}/api/field-reports/${reportId}?user_id=${user?.id}`,
          { audio_url: completeRes.data.url, transcription_status: 'pending' },
          { headers: getAuthHeaders() }
        );
        console.log('Chunked upload complete:', completeRes.data.url);
      } else {
        // Direct upload for smaller files (with retry for spotty connections)
        audioFormData.append('file', mediaBlob, fileName);
        
        const uploadResponse = await axiosWithRetry({
          method: 'post',
          url: `${BACKEND_URL}/api/field-reports/${reportId}/upload-audio?user_id=${user?.id}`,
          data: audioFormData,
          headers: getAuthHeaders(),
          timeout: 300000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log('Upload progress:', percentCompleted + '%');
          }
        });
        
        console.log('Upload response:', uploadResponse.data);
        
        if (!uploadResponse.data.success) {
          throw new Error(uploadResponse.data.message || 'Upload failed');
        }
      }
      
      // Upload photos
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        console.log(`Uploading photo ${i + 1}/${photos.length}`);
        const photoFormData = new FormData();
        photoFormData.append('file', photo.file);
        photoFormData.append('description', photo.description || '');
        
        await axios.post(
          `${BACKEND_URL}/api/field-reports/${reportId}/upload-photo?user_id=${user?.id}`,
          photoFormData,
          { 
            headers: getAuthHeaders(),
            timeout: 60000 // 1 minute timeout for photos
          }
        );
      }
      
      setIsUploading(false);
      setIsTranscribing(true);
      console.log('Starting transcription...');
      
      // Transcribe audio — non-blocking. If this fails, recording is still saved.
      let transcriptionText = '';
      try {
        const transcribeResponse = await axios.post(
          `${BACKEND_URL}/api/field-reports/${reportId}/transcribe?user_id=${user?.id}`,
          {},
          { 
            headers: getAuthHeaders(),
            timeout: 180000 // 3 minute timeout for transcription
          }
        );
        
        console.log('Transcription response:', transcribeResponse.data);
        transcriptionText = transcribeResponse.data.transcription || '';
      } catch (transcribeErr) {
        console.error('Transcription failed (recording is still saved):', transcribeErr);
        // Don't throw — the recording is already uploaded and saved
      }
      
      setIsTranscribing(false);
      
      if (transcriptionText) {
        setTranscription(transcriptionText);
      } else {
        setTranscription('[Transcription unavailable — recording saved successfully]');
      }
      
      // If linked to a lead, save the recording and transcription to lead files
      if (leadId) {
        console.log('Saving recording to lead files...');
        try {
          // Save the video/audio to lead files
          const leadFileFormData = new FormData();
          const mediaFileName = recordingMode === 'video' 
            ? `fieldcam_video_${new Date().toISOString().slice(0,10)}.${fileExtension}`
            : `fieldcam_audio_${new Date().toISOString().slice(0,10)}.${fileExtension}`;
          leadFileFormData.append('file', mediaBlob, mediaFileName);
          
          await axios.post(
            `${BACKEND_URL}/api/crm/leads/${leadId}/files?category=fieldcam`,
            leadFileFormData,
            { 
              headers: getAuthHeaders(),
              timeout: 300000
            }
          );
          console.log('Recording saved to lead files');
          
          // Save transcription as a text file (only if we got one)
          if (transcriptionText && !transcriptionText.startsWith('[')) {
            const transcriptBlob = new Blob([
              `FieldCam Transcription - ${new Date().toLocaleString()}\n`,
              `Property: ${propertyAddress}\n`,
              `Recording Type: ${recordingMode === 'video' ? 'Video' : 'Audio'}\n`,
              `Duration: ${formatTime(recordingTime)}\n\n`,
              `--- Transcription ---\n\n`,
              transcriptionText
            ], { type: 'text/plain' });
            
            const transcriptFormData = new FormData();
            transcriptFormData.append('file', transcriptBlob, `fieldcam_transcript_${new Date().toISOString().slice(0,10)}.txt`);
            
            await axios.post(
              `${BACKEND_URL}/api/crm/leads/${leadId}/files?category=fieldcam`,
              transcriptFormData,
              { headers: getAuthHeaders() }
            );
            console.log('Transcription saved to lead files');
          }
          
          // Save photos to lead files
          for (let i = 0; i < photos.length; i++) {
            const photo = photos[i];
            const photoFormData = new FormData();
            photoFormData.append('file', photo.file);
            
            await axios.post(
              `${BACKEND_URL}/api/crm/leads/${leadId}/files?category=photo`,
              photoFormData,
              { headers: getAuthHeaders() }
            );
          }
          if (photos.length > 0) {
            console.log(`${photos.length} photos saved to lead files`);
          }
          
        } catch (leadFileErr) {
          console.error('Failed to save to lead files:', leadFileErr);
        }
      }
      
      setCurrentStep('review');
      
    } catch (err) {
      console.error('Processing error:', err);
      setIsUploading(false);
      setIsTranscribing(false);
      
      // Provide more helpful error messages
      let errorMessage = 'Processing failed. Please try again.';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Upload timed out. Please check your connection and try again.';
      } else if (!err.response && err.message === 'Network Error') {
        errorMessage = 'Network error — could not reach the server. Check your cell/WiFi connection and tap "Process Recording" again. Your recording is still saved locally.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (err.response?.status === 413) {
        errorMessage = 'File rejected by server. Please try again or contact support.';
      } else if (err.response?.status === 400) {
        errorMessage = err.response.data?.detail || 'Invalid file format. Try recording again.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };
  
  // Generate AI report
  const generateReport = async () => {
    if (!fieldReportId) {
      setError('No field report found. Please record again.');
      return;
    }
    
    setIsGeneratingReport(true);
    setError('');
    
    try {
      // First, ensure the transcription is saved to the DB
      // (handles case where transcription was edited or only in frontend state)
      if (transcription && transcription !== '[Transcription unavailable — recording saved successfully]') {
        await axios.put(
          `${BACKEND_URL}/api/field-reports/${fieldReportId}?user_id=${user?.id}`,
          { transcription: transcription, transcription_status: 'completed' },
          { headers: getAuthHeaders() }
        ).catch(() => {}); // Best-effort save
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/field-reports/${fieldReportId}/generate-report?user_id=${user?.id}`,
        { transcription_text: transcription }, // Also pass as body in case DB save failed
        { headers: getAuthHeaders() }
      );
      
      setAiReport(response.data.report);
      setCurrentStep('report');
      
    } catch (err) {
      console.error('Report generation error:', err);
      const detail = err.response?.data?.detail || '';
      if (detail.includes('still processing') || detail.includes('pending')) {
        setError('Your recording is still being processed. The report will be saved automatically — check your Field Reports shortly.');
        setCurrentStep('review');
      } else {
        setError(detail || 'Report generation failed. Please try again.');
      }
    } finally {
      setIsGeneratingReport(false);
    }
  };
  
  // Export Xactimate format
  const exportXactimate = async () => {
    if (!fieldReportId) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/field-reports/${fieldReportId}/export-xactimate?user_id=${user?.id}`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `field_report_xactimate.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (err) {
      setError('Export failed. Please try again.');
    }
  };
  
  // Attach to claim
  const attachToClaim = async (targetClaimId) => {
    if (!fieldReportId) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/field-reports/${fieldReportId}/attach-to-claim?user_id=${user?.id}&claim_id=${targetClaimId}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      navigate(`/dashboard/claims/${targetClaimId}`);
      
    } catch (err) {
      setError('Failed to attach to claim. Please try again.');
    }
  };
  
  // Render recording step
  const renderRecordStep = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-4">
      {/* Recording Mode Toggle */}
      <div className="flex items-center gap-2 mb-6 bg-gray-800 rounded-full p-1">
        <button
          onClick={() => !isRecording && setRecordingMode('audio')}
          disabled={isRecording}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
            recordingMode === 'audio' 
              ? 'bg-orange-500 text-white' 
              : 'text-gray-400 hover:text-white'
          } ${isRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Mic className="w-4 h-4" />
          Audio Only
        </button>
        <button
          onClick={() => !isRecording && setRecordingMode('video')}
          disabled={isRecording}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
            recordingMode === 'video' 
              ? 'bg-orange-500 text-white' 
              : 'text-gray-400 hover:text-white'
          } ${isRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Video className="w-4 h-4" />
          Video + Audio
        </button>
      </div>
      
      {/* Test Recording Section */}
      {!isRecording && !audioBlob && !videoBlob && (
        <div className="w-full max-w-md mb-6">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">
                Test your {recordingMode === 'video' ? 'camera & microphone' : 'microphone'} first
              </span>
              <Button
                onClick={testRecording}
                disabled={isTesting}
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="test-recording-btn"
              >
                {isTesting ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <Camera className="w-3 h-3 mr-1" />
                    Test Device
                  </>
                )}
              </Button>
            </div>
            
            {/* Test video preview (only during testing in video mode) */}
            {isTesting && recordingMode === 'video' && (
              <div className="mb-3 rounded-lg overflow-hidden bg-black">
                <video 
                  ref={testVideoRef}
                  autoPlay 
                  muted 
                  playsInline
                  className="w-full h-32 object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
              </div>
            )}
            
            {/* Test result message */}
            {testMessage && (
              <div className={`text-sm p-2 rounded ${
                testResult === 'success' 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                  : testResult === 'failed'
                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : testResult === 'warning'
                      ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              }`}>
                {testMessage}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Video Preview — always mount the element so camera connects instantly */}
      {recordingMode === 'video' && (
        <div className={`w-full max-w-lg mb-6 rounded-xl overflow-hidden bg-black relative shadow-2xl border-2 border-gray-700 ${!isRecording && !videoUrl ? 'hidden' : ''}`}>
          {isRecording ? (
            <>
              <video 
                ref={videoRef}
                autoPlay 
                muted 
                playsInline
                webkit-playsinline="true"
                className="w-full h-64 object-cover"
              />
              {/* Recording indicator overlay */}
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-600 px-3 py-1.5 rounded-full shadow-lg">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                <span className="text-white text-sm font-medium">REC</span>
                <span className="text-white/80 text-sm">{formatTime(recordingTime)}</span>
              </div>
              <div className="absolute bottom-3 right-3 bg-black/60 px-2 py-1 rounded text-xs text-gray-300">
                Live Preview
              </div>
            </>
          ) : videoUrl ? (
            <video 
              src={videoUrl}
              controls
              playsInline
              className="w-full h-64 object-cover"
            />
          ) : null}
        </div>
      )}
      
      {/* Always-mounted hidden video element for camera stream (fixes black screen on mobile) */}
      {recordingMode === 'video' && !isRecording && !videoUrl && (
        <video ref={videoRef} autoPlay muted playsInline webkit-playsinline="true" className="hidden" />
      )}
      
      {/* Recording indicator - show smaller version when video is recording */}
      {!(recordingMode === 'video' && isRecording) && (
        <div className={`w-48 h-48 rounded-full flex items-center justify-center mb-8 transition-all ${
          isRecording 
            ? 'bg-red-500/20 animate-pulse' 
            : (audioBlob || videoBlob)
              ? 'bg-green-500/20' 
              : 'bg-gray-800'
        }`}>
          {isRecording ? (
            <div className="text-center">
              <div className="text-4xl font-mono text-red-500 mb-2">
                {formatTime(recordingTime)}
              </div>
              <div className="flex items-center gap-2 text-red-400 justify-center">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                {isPaused ? 'Paused' : (recordingMode === 'video' ? 'Recording Video...' : 'Recording...')}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {formatTime(300 - recordingTime)} remaining (5 min max)
              </div>
              {recordingTime >= 270 && (
                <div className="text-xs text-amber-400 mt-1 animate-pulse">Auto-stops in {300 - recordingTime}s</div>
              )}
            </div>
          ) : (audioBlob || videoBlob) ? (
            <div className="text-center">
              <Check className="w-16 h-16 text-green-500 mx-auto mb-2" />
              <div className="text-green-400">{formatTime(recordingTime)} recorded</div>
              <div className="text-green-400/70 text-sm">{recordingMode === 'video' ? '(Video)' : '(Audio)'}</div>
            </div>
          ) : (
            recordingMode === 'video' ? <Video className="w-16 h-16 text-gray-500" /> : <Mic className="w-16 h-16 text-gray-500" />
          )}
        </div>
      )}
      
      {/* Compact recording status for video mode */}
      {recordingMode === 'video' && isRecording && (
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center gap-3 text-red-400">
            <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-lg font-medium">Recording Video</span>
          </div>
          <p className="text-gray-400 text-sm mt-1">Speak clearly about the damage you see</p>
        </div>
      )}
      
      {/* Recording controls */}
      <div className="flex items-center gap-4 mb-8">
        {/* Hidden video file upload input */}
        <input
          ref={videoUploadRef}
          type="file"
          accept="video/*,audio/*"
          capture={false}
          onChange={handleVideoFileUpload}
          className="hidden"
          data-testid="video-upload-input"
        />
        
        {!isRecording && !audioBlob && !videoBlob && (
          <>
            <Button 
              onClick={startRecording}
              className="bg-red-500 hover:bg-red-600 text-white px-8 py-6 rounded-full text-lg"
              data-testid="start-recording-btn"
            >
              {recordingMode === 'video' ? <Video className="w-6 h-6 mr-2" /> : <Mic className="w-6 h-6 mr-2" />}
              Start {recordingMode === 'video' ? 'Video' : 'Recording'}
            </Button>
            <Button
              onClick={() => videoUploadRef.current?.click()}
              variant="outline"
              className="px-6 py-6 rounded-full text-lg border-orange-500 text-orange-400 hover:bg-orange-500/10"
              data-testid="upload-video-btn"
            >
              <Upload className="w-6 h-6 mr-2" />
              Upload Video
            </Button>
          </>
        )}
        
        {isRecording && (
          <>
            <Button 
              onClick={togglePause}
              variant="outline"
              className="px-6 py-6 rounded-full"
              data-testid="pause-recording-btn"
            >
              {isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
            </Button>
            <Button 
              onClick={stopRecording}
              className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-6 rounded-full"
              data-testid="stop-recording-btn"
            >
              <StopCircle className="w-6 h-6 mr-2" />
              Stop
            </Button>
          </>
        )}
        
        {(audioBlob || videoBlob) && !isRecording && (
          <>
            <Button 
              onClick={clearRecording}
              variant="outline"
              className="px-6 py-6 rounded-full border-red-500 text-red-500"
              data-testid="clear-recording-btn"
            >
              <Trash2 className="w-6 h-6" />
            </Button>
            {recordingMode === 'audio' && audioUrl && (
              <Button 
                onClick={() => {
                  const audio = new Audio(audioUrl);
                  audio.play();
                }}
                variant="outline"
                className="px-6 py-6 rounded-full"
                data-testid="play-recording-btn"
              >
                <Volume2 className="w-6 h-6" />
              </Button>
            )}
          </>
        )}
      </div>
      
      {/* Tips */}
      <div className="bg-gray-800/50 rounded-lg p-4 max-w-md text-sm text-gray-400 mb-8">
        <p className="font-medium text-white mb-2">
          {recordingMode === 'video' ? 'Video Recording Tips:' : 'Recording Tips:'}
        </p>
        <ul className="space-y-1 list-disc list-inside">
          {recordingMode === 'video' ? (
            <>
              <li>Point camera at damage while describing what you see</li>
              <li>Walk slowly around the roof for steady footage</li>
              <li>Speak clearly - your narration will be transcribed</li>
              <li>Video is processed for audio transcription</li>
            </>
          ) : (
            <>
              <li>Walk around the roof describing damage you see</li>
              <li>Mention locations: "North slope", "Near chimney"</li>
              <li>Describe damage type: hail, wind, water</li>
              <li>Note severity: minor, moderate, severe</li>
            </>
          )}
        </ul>
      </div>
      
      {/* Photo upload section */}
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Photos ({photos.length})</h3>
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            data-testid="add-photos-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Add Photos
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            capture="environment"
            onChange={handlePhotoUpload}
            className="hidden"
          />
        </div>
        
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            {photos.map(photo => (
              <div key={photo.id} className="relative group">
                <img 
                  src={photo.preview} 
                  alt="Damage" 
                  className="w-full h-24 object-cover rounded-lg"
                />
                <button
                  onClick={() => removePhoto(photo.id)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Property address */}
      <div className="w-full max-w-md mb-6">
        <label className="block text-sm text-gray-400 mb-2">Property Address (optional)</label>
        <AddressAutocomplete
          value={propertyAddress}
          onChange={(val) => setPropertyAddress(val)}
          onSelectAddress={(addr) => setPropertyAddress(addr.display_name || addr)}
          placeholder="Start typing an address..."
          className="bg-gray-800 border-gray-700 text-white"
          data-testid="property-address-input"
        />
      </div>
      
      {/* Process button */}
      {(audioBlob || videoBlob) && (
        <Button 
          onClick={processRecording}
          disabled={isUploading || isTranscribing}
          className="w-full max-w-md bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
          data-testid="process-recording-btn"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Uploading {recordingMode === 'video' ? 'Video' : 'Audio'}...
            </>
          ) : isTranscribing ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Transcribing with AI...
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 mr-2" />
              Process {recordingMode === 'video' ? 'Video' : 'Recording'}
            </>
          )}
        </Button>
      )}
      
      {error && (
        <div className="mt-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 max-w-md">
          {error}
        </div>
      )}
      
      {/* Always show link to saved reports */}
      {fieldReportId && (
        <div className="mt-3">
          <button 
            onClick={() => navigate('/dashboard/field-reports')}
            className="text-sm text-blue-400 hover:text-blue-300 underline"
            data-testid="view-saved-reports-link"
          >
            View all saved field reports
          </button>
        </div>
      )}
    </div>
  );
  
  // Render review step
  const renderReviewStep = () => (
    <div className="p-4 max-w-2xl mx-auto">
      <h2 className="text-xl font-bold mb-4">Review Transcription</h2>
      
      <div className="bg-gray-800 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">AI Transcription</span>
          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">
            Whisper AI
          </span>
        </div>
        <textarea
          value={transcription}
          onChange={(e) => setTranscription(e.target.value)}
          rows={8}
          className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white resize-none focus:outline-none focus:border-orange-500"
          placeholder="Your transcribed notes will appear here..."
          data-testid="transcription-textarea"
        />
        <p className="text-xs text-gray-500 mt-2">
          You can edit the transcription before generating the report.
        </p>
      </div>
      
      {/* Photos preview */}
      {photos.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3">Photos ({photos.length})</h3>
          <div className="grid grid-cols-2 gap-3">
            {photos.map(photo => (
              <div key={photo.id} className="bg-gray-800 rounded-lg p-2">
                <img 
                  src={photo.preview} 
                  alt="Damage" 
                  className="w-full h-32 object-cover rounded mb-2"
                />
                <input
                  type="text"
                  value={photo.description}
                  onChange={(e) => updatePhotoDescription(photo.id, e.target.value)}
                  placeholder="Describe this photo..."
                  className="w-full px-2 py-1 text-sm bg-gray-900 border border-gray-700 rounded focus:outline-none focus:border-orange-500"
                />
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex gap-4">
        <Button 
          variant="outline"
          onClick={() => setCurrentStep('record')}
          className="flex-1"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Recording
        </Button>
        <Button 
          onClick={generateReport}
          disabled={isGeneratingReport || !transcription}
          className="flex-1 bg-orange-500 hover:bg-orange-600"
          data-testid="generate-report-btn"
        >
          {isGeneratingReport ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating AI Report...
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              Generate Damage Report
            </>
          )}
        </Button>
      </div>
      
      {error && (
        <div className="mt-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400">
          {error}
          <button 
            onClick={() => navigate('/dashboard/field-reports')}
            className="block mt-2 text-sm text-blue-400 hover:text-blue-300 underline"
          >
            Check saved field reports — your report may already be there
          </button>
        </div>
      )}
    </div>
  );
  
  // Render report step
  const renderReportStep = () => (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">AI Damage Report</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={exportXactimate}
            data-testid="export-xactimate-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Xactimate
          </Button>
          {!claimId && (
            <Button 
              variant="outline"
              onClick={() => navigate('/dashboard/claims')}
              data-testid="attach-to-claim-btn"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Attach to Claim
            </Button>
          )}
        </div>
      </div>
      
      {aiReport && (
        <div className="space-y-6">
          {/* Report Actions Bar */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-green-400 font-medium">Report Generated</span>
              {fieldReportId && (
                <span className="text-gray-500 text-sm ml-2">ID: {fieldReportId.slice(0, 8)}...</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button 
                size="sm"
                onClick={downloadReport}
                variant="outline"
                className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                data-testid="download-report-btn"
              >
                <Download className="w-4 h-4 mr-1" /> Download Report
              </Button>
              <Button
                size="sm"
                onClick={() => navigate('/dashboard/field-reports')}
                variant="outline"
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                data-testid="view-saved-reports-btn"
              >
                <FileText className="w-4 h-4 mr-1" /> View All Reports
              </Button>
            </div>
          </div>
          {/* Summary */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="font-medium text-orange-400 mb-2">Summary</h3>
            <p className="text-gray-300">{aiReport.summary}</p>
            <div className="mt-3 flex items-center gap-4">
              <span className={`px-3 py-1 rounded-full text-sm ${
                aiReport.urgency === 'critical' ? 'bg-red-500/20 text-red-400' :
                aiReport.urgency === 'high' ? 'bg-orange-500/20 text-orange-400' :
                aiReport.urgency === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-green-500/20 text-green-400'
              }`}>
                {aiReport.urgency?.toUpperCase()} URGENCY
              </span>
            </div>
          </div>
          
          {/* Damage Areas */}
          {aiReport.damage_areas?.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-medium text-orange-400 mb-3">Damage Areas Identified</h3>
              <div className="space-y-3">
                {aiReport.damage_areas.map((area, i) => (
                  <div key={i} className="bg-gray-900 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{area.location}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        area.severity === 'severe' ? 'bg-red-500/20 text-red-400' :
                        area.severity === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {area.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mb-2">{area.description}</p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500">Type:</span>
                      <span className="text-gray-300">{area.damage_type}</span>
                      <span className="text-gray-500 ml-2">Action:</span>
                      <span className={`${
                        area.recommended_action === 'replace' ? 'text-red-400' :
                        area.recommended_action === 'repair' ? 'text-yellow-400' :
                        'text-green-400'
                      }`}>{area.recommended_action}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Materials Affected */}
          {aiReport.materials_affected?.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-medium text-orange-400 mb-3">Materials Affected</h3>
              <div className="grid gap-2">
                {aiReport.materials_affected.map((mat, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-900 rounded p-2">
                    <span>{mat.material}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">{mat.condition}</span>
                      {mat.replacement_needed && (
                        <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">
                          Replace
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Xactimate Codes */}
          {aiReport.xactimate_codes?.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-medium text-orange-400 mb-3">Suggested Xactimate Codes</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-700">
                      <th className="pb-2">Code</th>
                      <th className="pb-2">Description</th>
                      <th className="pb-2">Est. Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiReport.xactimate_codes.map((code, i) => (
                      <tr key={i} className="border-b border-gray-700/50">
                        <td className="py-2 font-mono text-orange-400">{code.code}</td>
                        <td className="py-2 text-gray-300">{code.description}</td>
                        <td className="py-2 text-gray-400">{code.estimated_quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Recommendations */}
          {aiReport.recommendations?.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-medium text-orange-400 mb-3">Recommendations</h3>
              <ul className="space-y-2">
                {aiReport.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-300">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Insurance Notes */}
          {aiReport.insurance_notes && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <h3 className="font-medium text-blue-400 mb-2">Insurance Claim Notes</h3>
              <p className="text-gray-300 text-sm">{aiReport.insurance_notes}</p>
            </div>
          )}
        </div>
      )}
      
      <div className="mt-8 flex gap-4">
        <Button 
          variant="outline"
          onClick={() => {
            setCurrentStep('record');
            clearRecording();
            setPhotos([]);
            setAiReport(null);
            setFieldReportId(null);
          }}
          className="flex-1"
          data-testid="new-report-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          New Report
        </Button>
        {claimId && (
          <Button 
            onClick={() => navigate(`/dashboard/claims/${claimId}`)}
            className="flex-1 bg-orange-500 hover:bg-orange-600"
          >
            Return to Claim
          </Button>
        )}
        {leadId && (
          <Button 
            onClick={() => navigate('/dashboard/pipeline')}
            className="flex-1 bg-orange-500 hover:bg-orange-600"
          >
            Return to Pipeline
          </Button>
        )}
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="field-cam-page">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-gray-400"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="text-center">
            <h1 className="text-lg font-bold text-orange-400">FieldCam AI</h1>
            {leadId && leadName && (
              <p className="text-xs text-gray-400 mt-0.5">
                Recording for: <span className="text-white">{decodeURIComponent(leadName)}</span>
              </p>
            )}
          </div>
          <div className="w-20"></div>
        </div>
      </div>
      
      {/* Lead/Claim info banner */}
      {(leadId || claimId) && (
        <div className="bg-orange-500/10 border-b border-orange-500/30 px-4 py-2">
          <div className="max-w-3xl mx-auto flex items-center justify-center gap-2 text-sm">
            {leadId ? (
              <>
                <span className="text-orange-400">📁</span>
                <span className="text-orange-300">
                  Recording will automatically save to {decodeURIComponent(leadName || 'Lead')}'s file
                </span>
              </>
            ) : claimId && (
              <>
                <span className="text-orange-400">📋</span>
                <span className="text-orange-300">Linked to Insurance Claim</span>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Step indicator */}
      <div className="bg-gray-800/50 py-3">
        <div className="flex justify-center gap-8 max-w-md mx-auto">
          {['record', 'review', 'report'].map((step, i) => (
            <div key={step} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                currentStep === step 
                  ? 'bg-orange-500 text-white' 
                  : ['record', 'review', 'report'].indexOf(currentStep) > i
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-700 text-gray-400'
              }`}>
                {['record', 'review', 'report'].indexOf(currentStep) > i ? (
                  <Check className="w-4 h-4" />
                ) : (
                  i + 1
                )}
              </div>
              <span className={`text-sm capitalize ${
                currentStep === step ? 'text-white' : 'text-gray-500'
              }`}>
                {step}
              </span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Main content */}
      <div className="pb-20">
        {currentStep === 'record' && renderRecordStep()}
        {currentStep === 'review' && renderReviewStep()}
        {currentStep === 'report' && renderReportStep()}
      </div>
    </div>
  );
};

export default FieldCam;
